import { trackError } from "../../utils/analytics/track-error";

const EXPECTED_FIREBASE_SESSION_STORAGE_KEY = "jungle_expected_firebase_session_v1";

type ExpectedFirebaseSession = {
  userId: string;
};

let hasReportedUnexpectedSessionLoss = false;
let hasReportedSessionStorageFailure = false;

function reportSessionStorageFailure(error: unknown, operation: "read" | "write"): void {
  if (hasReportedSessionStorageFailure) return;
  hasReportedSessionStorageFailure = true;

  trackError(error, {
    scope: "firebase-auth-session-persistence",
    level: "warning",
    fingerprint: ["firebase-auth-session-persistence", `storage-${operation}-failed`],
    tags: {
      feature: "authentication",
      operation,
      is_native_webview: String(Boolean(window.ReactNativeWebView)),
    },
  });
}

function readExpectedFirebaseSession(): ExpectedFirebaseSession | null {
  let storedSession: string | null;
  try {
    storedSession = localStorage.getItem(EXPECTED_FIREBASE_SESSION_STORAGE_KEY);
  } catch (error) {
    reportSessionStorageFailure(error, "read");
    return null;
  }

  if (!storedSession) return null;

  try {
    const parsedSession = JSON.parse(storedSession) as Partial<ExpectedFirebaseSession>;
    return typeof parsedSession.userId === "string" && parsedSession.userId ? { userId: parsedSession.userId } : null;
  } catch {
    return null;
  }
}

export function markFirebaseSessionAsExpected(userId: string): void {
  if (!userId) return;

  try {
    localStorage.setItem(EXPECTED_FIREBASE_SESSION_STORAGE_KEY, JSON.stringify({ userId }));
  } catch (error) {
    reportSessionStorageFailure(error, "write");
  }
}

export function markFirebaseSessionEndAsExpected(): void {
  try {
    localStorage.removeItem(EXPECTED_FIREBASE_SESSION_STORAGE_KEY);
  } catch {
    // A storage failure is already diagnosed on the corresponding read/write.
  }
}

export function reportUnexpectedFirebaseSessionLoss(): void {
  if (hasReportedUnexpectedSessionLoss) return;

  const expectedFirebaseSession = readExpectedFirebaseSession();
  if (!expectedFirebaseSession) return;

  hasReportedUnexpectedSessionLoss = true;
  markFirebaseSessionEndAsExpected();
  trackError(new Error("A previously authenticated Firebase session was not restored"), {
    scope: "firebase-auth-session-not-restored",
    level: "warning",
    userId: expectedFirebaseSession.userId,
    fingerprint: ["firebase-auth-session-not-restored"],
    tags: {
      feature: "authentication",
      is_native_webview: String(Boolean(window.ReactNativeWebView)),
      document_visibility: document.visibilityState,
    },
  });
}

export function resetAuthSessionObservabilityForTests(): void {
  hasReportedUnexpectedSessionLoss = false;
  hasReportedSessionStorageFailure = false;
}
